import { getAccessToken } from '../supabaseClient';
import type { SiteDraftPayload, SiteDraftRecord } from './drafts';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? 'http://localhost:4000';

const buildUrl = (path: string) => {
  const base = API_BASE_URL.replace(/\/*$/, '');
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${base}${normalizedPath}`;
};

const extractErrorMessage = (details: unknown, fallback: string): string => {
  if (!details) {
    return fallback;
  }

  if (typeof details === 'string' && details.trim().length > 0) {
    return details;
  }

  if (typeof details === 'object' && details !== null) {
    const record = details as Record<string, unknown>;
    for (const key of ['message', 'error', 'details'] as const) {
      const value = record[key];
      if (typeof value === 'string' && value.trim().length > 0) {
        return value;
      }
    }
  }

  return fallback;
};

export class AuthTokenExpiredError extends Error {
  constructor(message = 'Your session has expired. Please sign in again.') {
    super(message);
    this.name = 'AuthTokenExpiredError';
  }
}

export class AccessDeniedError extends Error {
  constructor(message = 'You do not have permission to perform this action.') {
    super(message);
    this.name = 'AccessDeniedError';
  }
}

export type SiteBasePayload = Omit<SiteDraftPayload, 'id'>;

export type SiteCreatePayload = SiteBasePayload & { draftId?: string };

export type SiteUpdatePayload = SiteBasePayload & { id: string };

export interface SiteRecord extends SiteDraftRecord {}

interface SitesResponse {
  sites: SiteRecord[];
}

interface SiteResponse {
  site: SiteRecord;
}

export const fetchAccessibleSites = async (): Promise<SiteRecord[]> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/sites/accessible'), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to fetch accessible sites.');
    throw new Error(message);
  }

  const { sites } = (await response.json()) as SitesResponse;
  return sites;
};

export const createSite = async (payload: SiteCreatePayload): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/sites'), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to save site.');
    const error = new Error(message);
    throw error;
  }

  const { site } = (await response.json()) as { site: SiteRecord };
  return site;
};

export const updateSite = async (payload: SiteUpdatePayload): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/sites/${payload.id}`), {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(payload)
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to update site.');
    throw new Error(message);
  }

  const { site } = (await response.json()) as SiteResponse;
  return site;
};

const RETRYABLE_STATUS_CODES = new Set([408, 429, 500, 502, 503, 504]);
const MAX_RETRIES = 3;

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

export const fetchSites = async (attempt = 0): Promise<SiteRecord[]> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl('/api/sites'), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const status = response.status;
    if (RETRYABLE_STATUS_CODES.has(status) && attempt < MAX_RETRIES) {
      const retryCount = attempt + 1;
      const backoffMs = Math.min(2000, 250 * 2 ** retryCount);
      await delay(backoffMs);
      return fetchSites(retryCount);
    }

    const message = extractErrorMessage(details, `Failed to fetch sites (status ${status}).`);
    throw new Error(message);
  }

  const { sites } = (await response.json()) as SitesResponse;
  return sites;
};

export const fetchSiteById = async (siteId: string): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/sites/${siteId}`), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to fetch site.');
    throw new Error(message);
  }

  const { site } = (await response.json()) as SiteResponse;
  return site;
};

export const fetchAccessibleSiteById = async (siteId: string): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/sites/accessible/${siteId}`), {
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }
    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to fetch site.');
    throw new Error(message);
  }

  const { site } = (await response.json()) as SiteResponse;
  return site;
};

export const shareSiteWithUser = async (siteId: string, userId: string): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/sites/${siteId}/share`), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ userId })
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to share site with client.');
    throw new Error(message);
  }

  const { site } = (await response.json()) as SiteResponse;
  return site;
};

export const unshareSiteWithUser = async (siteId: string, userId: string): Promise<SiteRecord> => {
  const token = await getAccessToken();
  const response = await fetch(buildUrl(`/api/sites/${siteId}/share/${userId}`), {
    method: 'DELETE',
    headers: {
      Authorization: `Bearer ${token}`
    }
  });

  if (!response.ok) {
    if (response.status === 401) {
      throw new AuthTokenExpiredError();
    }
    if (response.status === 403) {
      throw new AccessDeniedError();
    }

    let details: unknown = null;
    try {
      details = await response.json();
    } catch (error) {
      // ignore parsing errors
    }

    const message = extractErrorMessage(details, 'Failed to revoke site access.');
    throw new Error(message);
  }

  const { site } = (await response.json()) as SiteResponse;
  return site;
};
